import React from 'react';
import {
    TextField,
    Button,
    Paper,
    Typography,
    Stack,
    Box,
} from '@mui/material';
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useNotification} from "../../utils/hooks/useNotification";
import {postFetcher} from "../../utils/api/fetchers";
import {useNavigate} from "react-router-dom";
import {CategoryCreateRequest, categoryCreateSchema} from "../../schemas/category/categoryCreateSchema";
import {Category} from "../../schemas/category/categoriesSchema";
import CustomRichTextEditor from "../../components/input/CustomRichTextEditor";

const CategoryCreate: React.FC = () => {
    const navigate = useNavigate();
    const {showNotification} = useNotification();
    const {register, handleSubmit, watch, setValue, formState: {errors}} = useForm<CategoryCreateRequest>({
        resolver: zodResolver(categoryCreateSchema),
    });

    const onSubmit = async (data: CategoryCreateRequest) => {
        postFetcher<CategoryCreateRequest, Category>("/categories", data)
            .then(() => {
                showNotification("Kategorie byla úspěšně vytvořena", "success");
                navigate('/categories');
            })
            .catch((error) => {
                if (error.response?.status === 409) {
                    showNotification("Kategorie s tímto jménem nebo číslem již existuje", "error");
                } else {
                    showNotification("Nepodařilo se vytvořit kategorii", "error");
                }
            });
    };

    return (
        <Paper elevation={3} sx={{padding: 4, maxWidth: 600, margin: '0 auto'}}>
            <Typography variant="h4" gutterBottom>
                Vytvoření kategorie
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Jméno"
                        variant="standard"
                        required
                        {...register('name')}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                    />
                    <TextField
                        fullWidth
                        label="Číslo kategorie"
                        variant="standard"
                        type="number"
                        required
                        {...register('categoryNumber', {valueAsNumber: true})}
                        error={!!errors.categoryNumber}
                        helperText={errors.categoryNumber?.message}
                        InputProps={{inputProps: {min: 1}}}
                    />
                    <CustomRichTextEditor
                        title="Popis"
                        value={watch('description') ?? undefined}
                        onChange={(value) => setValue('description', value, {shouldDirty: true})}
                        error={errors.description?.message}
                    />
                    <Button type="submit" variant="contained" color="primary" fullWidth>
                        Vytvořit kategorii
                    </Button>
                </Stack>
            </Box>
        </Paper>
    );
};

export default CategoryCreate;